import React from 'react';
import { 
  Typography, 
  Container, 
  Box, 
  Paper, 
  List, 
  ListItem, 
  Link,
  Fab,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  useTheme,
  Stack
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InfoIcon from '@mui/icons-material/Info';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CancelIcon from '@mui/icons-material/Cancel';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const ScrollToTopButton = () => {
  const [visible, setVisible] = React.useState(false);
  const [fade, setFade] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setFade(true);
    setTimeout(() => {
      setVisible(false);
      setFade(false);
    }, 500);
  };

  if (!visible) return null;

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        opacity: fade ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      <Fab color="primary" aria-label="scroll back to top">
        <KeyboardArrowUpIcon />
      </Fab>
    </Box>
  );
};

// Section header component
const SectionHeader = ({ icon: Icon, title, id }) => {
  const theme = useTheme();
  return (
    <Box id={id} sx={{ mt: 6, mb: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 1,
        pb: 1,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Icon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="h5" component="h2" fontWeight="500">
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

// Tip component
const ProTip = ({ children }) => {
  return (
    <Box 
      sx={{ 
        p: 2, 
        my: 3, 
        borderRadius: 2, 
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
        border: '1px solid rgba(25, 118, 210, 0.2)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <LightbulbIcon 
          color="primary" 
          sx={{ 
            mr: 1.5, 
            mt: 0.5,
            fontSize: 24
          }} 
        />
        <Typography variant="body1" color="primary.dark">
          {children}
        </Typography>
      </Box>
    </Box>
  );
};

// Commission card component
const CommissionCard = ({ title, price, directCommission, secondLevelCommission, isAnnual }) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[8]
        }
      }}
    >
      {isAnnual && (
        <Chip 
          label="SAVE 20%" 
          color="primary" 
          size="small" 
          sx={{ 
            position: 'absolute',
            top: -10,
            right: 20,
            fontWeight: 'bold'
          }} 
        />
      )}
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="h4" component="div" sx={{ mb: 3, fontWeight: '500' }}>
          {price}
        </Typography>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom color="text.secondary">
            Commissions:
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">Direct referral</Typography>
                <Typography variant="h6" color="primary.main">{directCommission}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Second-level</Typography>
                <Typography variant="h6" color="primary.main">{secondLevelCommission}</Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AffiliateProgram = () => {
  const theme = useTheme();
  
  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: { xs: 3, md: 5 }, my: 4, borderRadius: 2 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="500">
          Affiliate Program Terms
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        {/* Table of Contents */}
        <Paper 
          elevation={0} 
          sx={{ 
            my: 4, 
            p: 3, 
            bgcolor: 'background.subtle',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="h6" gutterBottom fontWeight="500">
            Table of Contents
          </Typography>
          <Box component="nav">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#overview" underline="hover" color="primary">
                      1. Overview
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#commission" underline="hover" color="primary">
                      2. Commission Structure
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#payment" underline="hover" color="primary">
                      3. Payment Scenarios and Policies
                    </Link>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#levels" underline="hover" color="primary">
                      4. Referral Levels
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#cancellation" underline="hover" color="primary">
                      5. Cancellation and Refund Policy
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#lapsed" underline="hover" color="primary">
                      6. Account Lapse and Re-Subscription
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#dormancy" underline="hover" color="primary">
                      7. Dormancy Clause
                    </Link>
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <Link href="#contact" underline="hover" color="primary">
                      8. Contact Information
                    </Link>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <SectionHeader id="overview" icon={InfoIcon} title="1. Overview" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          Our Affiliate Program rewards members for referring new users to ReaderFlex. Affiliates earn commissions on payments received from users they refer, including trial fees, subscription upgrades, and ad hoc purchases. This program supports referrals up to two levels deep.
        </Typography>

        <SectionHeader id="commission" icon={MonetizationOnIcon} title="2. Commission Structure" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          Commission earnings are based on the payment amount made by a referred user. We offer enhanced commissions for annual subscriptions to reward long-term value creation.
        </Typography>

        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <CommissionCard 
              title="ReaderFlex Membership (Monthly)"
              price="$9.99/month"
              directCommission="$3.00 per month"
              secondLevelCommission="$1.00 per month"
              isAnnual={false}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommissionCard 
              title="ReaderFlex Membership (Annual)"
              price="$79.99/year"
              directCommission="$24.00 upfront"
              secondLevelCommission="$8.00 upfront"
              isAnnual={true}
            />
          </Grid>
        </Grid>

        <ProTip>
          Encourage annual subscriptions! You'll earn more upfront, and your referrals get a better deal.
        </ProTip>

        <SectionHeader id="payment" icon={PaymentsIcon} title="3. Payment Scenarios and Policies" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          Here are some common scenarios and their commission structures:
        </Typography>

        <Grid container spacing={4} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ height: '100%', borderLeft: `4px solid ${theme.palette.primary.main}` }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="500">
                  Trial to Paid Conversion
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      When your referral converts from trial to any paid plan, you earn the standard commission amount for their chosen plan.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      Commissions begin with their first paid billing cycle.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ height: '100%', borderLeft: `4px solid ${theme.palette.success.main}` }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="500">
                  Direct Paid Signup
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      When your referral skips the trial and goes straight to a paid plan, you earn commission immediately.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      Annual subscriptions earn you a larger upfront commission.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ height: '100%', borderLeft: `4px solid ${theme.palette.info.main}` }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="500">
                  Plan Upgrades
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      If your referral upgrades their plan, your commission automatically adjusts to the new rate.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      For annual plan upgrades, you receive the difference in commission immediately.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: '500' }}>
          Commission Holding Period:
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  Monthly Subscriptions
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      Commissions are held for 30 days from the date of payment.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      This covers our 14-day money-back guarantee period and allows for any payment processing issues.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  Annual Subscriptions
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      Initial commissions are held for 30 days from the date of payment.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2">
                      Large commissions (over $500) may be released in installments over 60 days for security purposes.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Withdrawal Minimums
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Minimum withdrawal amount: $50
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Withdrawals can be requested once commissions clear the holding period.
                </Typography>
              </Box>
              <Box component="li">
                <Typography variant="body2">
                  Payment methods: PayPal, Bank Transfer, or Store Credit (+5% bonus)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <ProTip>
          Store credit offers a 5% bonus and instant availability, perfect for affiliates who also use ReaderFlex!
        </ProTip>

        <Typography paragraph sx={{ mt: 3, fontSize: '1.05rem', fontStyle: 'italic' }}>
          All commissions are calculated based on the net payment amount after any discounts or promotions. Commissions are only considered final after the holding period and when no refund has been issued.
        </Typography>

        <SectionHeader id="levels" icon={AccountTreeIcon} title="4. Referral Levels" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          The program supports two levels of referrals:
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'background.subtle', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">No Referrer:</Typography>
                    <Typography variant="body2" sx={{ pl: 2 }}>
                      Users signing up without an affiliate link generate no commission.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">First-Level Referral:</Typography>
                    <Typography variant="body2" sx={{ pl: 2 }}>
                      You receive <strong>$3.00/month</strong> for monthly subscriptions and <strong>$24.00/year</strong> for annual subscriptions on payments made by users you directly refer.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Second-Level Referral:</Typography>
                    <Typography variant="body2" sx={{ pl: 2 }}>
                      When a user you referred brings in a new user:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mt: 0.5 }}>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">
                          The new user's direct referrer earns $3.00/month or $24.00/year depending on their plan.
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">
                          You, as the original affiliate, earn an additional $1.00/month or $8.00/year.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <SectionHeader id="cancellation" icon={CancelIcon} title="5. Cancellation and Refund Policy" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          Our commission policy regarding cancellations and refunds:
        </Typography>
        
        <Box component="ul" sx={{ pl: 4, mt: 0 }}>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              If a user cancels their subscription, no further commissions will be accrued from that account.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              If a refund is issued during the money-back guarantee period, any pending commissions for that payment will be voided.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              For partial refunds, commissions will be adjusted proportionally.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Chargebacks or fraudulent activities will result in immediate commission reversal and possible program suspension.
            </Typography>
          </Box>
        </Box>
        
        <Card variant="outlined" sx={{ bgcolor: 'rgba(237, 108, 2, 0.05)', mt: 3, borderColor: 'warning.light' }}>
          <CardContent>
            <Typography paragraph sx={{ fontWeight: 'medium', color: 'warning.dark' }}>
              We reserve the right to modify the holding period or payment terms with 30 days notice to affiliates.
            </Typography>
          </CardContent>
        </Card>

        <SectionHeader id="lapsed" icon={RestartAltIcon} title="6. Account Lapse and Re-Subscription" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          If your own ReaderFlex subscription lapses, is canceled, or expires, you retain your affiliate status and referrals for a grace period of 90 days:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 0 }}>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              You keep all existing referrals, both direct and second-level.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Commissions continue to accrue when your referrals make payments, including during any period when your own subscription is inactive.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              You can still log in, view your affiliate dashboard, and request withdrawals.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              If you re-subscribe within 90 days, your affiliate data is fully preserved. There is no gap in commission earning and no need to re-establish your referral network.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Your affiliate link continues to work and new referrals can still sign up under you while your subscription is inactive.
            </Typography>
          </Box>
        </Box>

        <SectionHeader id="dormancy" icon={HourglassEmptyIcon} title="7. Dormancy Clause" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          To maintain active affiliate status, you must maintain an active or trialing ReaderFlex subscription. If your subscription remains inactive (canceled, expired, or unpaid) for more than 90 consecutive days, the following will occur automatically on the 91st day:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mt: 0 }}>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              All pending and available commissions are forfeited.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Your referrals are released. Referred users will no longer be linked to your account, and no future commissions will be generated from their payments.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Your affiliate link is deactivated and will no longer accept new referrals.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              Your earnings history and referral records are cleared from your account.
            </Typography>
          </Box>
        </Box>
        <Card variant="outlined" sx={{ bgcolor: 'rgba(237, 108, 2, 0.05)', mt: 3, borderColor: 'warning.light' }}>
          <CardContent>
            <Typography paragraph sx={{ fontWeight: 'medium', color: 'warning.dark' }}>
              This policy ensures that affiliate rewards go to active members of the ReaderFlex community. To avoid forfeiture, simply maintain an active subscription or re-subscribe within 90 days of your account lapsing.
            </Typography>
          </CardContent>
        </Card>

        <SectionHeader id="contact" icon={ContactSupportIcon} title="8. Contact Information" />
        <Typography paragraph variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
          For questions or further support regarding our Affiliate Program, please contact us at{' '}
          <Link href="mailto:affiliate@readerflex.com" underline="hover" color="primary" sx={{ fontWeight: 'medium' }}>
            affiliate@readerflex.com
          </Link>.
        </Typography>
      </Paper>
      
      {/* Scroll-to-top button */}
      <ScrollToTopButton />
    </Container>
  );
};

export default AffiliateProgram;
